import type { CanvasObject } from '../types';
import type { CanvasState } from '../hooks/useCanvasTransform';
import { getViewportWorld, intersectsRect } from '../utils/viewport';
import { getBorderCoordinates, alignForStroke } from '../utils/borderCoordinates';
import { drawRoundedRect, createRoundedRectPath, adjustCornerRadiusForBorder } from '../utils/roundedRect';
import { getAbsRotationValues } from '../utils/rotationCache';
import { getBlendModeOperation } from '../utils/blendMode';

interface ClipMaskMap {
  maskMap: Map<string, CanvasObject>;
  isMaskMap: Set<string>;
  clippedGroups: Map<string, CanvasObject[]>;
}

interface Artboard {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 아웃글로우 캐시 엔트리
 */
interface OutGlowCacheEntry {
  canvas: HTMLCanvasElement;
  glowX: number;
  glowY: number;
  hash: string;
  zoomLevel: number;
  renderScale: number;
  w: number;
  h: number;
  lastUsed: number;
}

/**
 * 아웃글로우 캐시 (객체 ID별, 최대 3개)
 */
const outGlowCache = new Map<string, OutGlowCacheEntry[]>();

/**
 * 블러 캐시 엔트리
 */
interface BlurCacheEntry {
  canvas: HTMLCanvasElement;
  hash: string;
  w: number;
  h: number;
}

/**
 * 블러 캐시 (캐시 키별)
 */
const blurCache = new Map<string, BlurCacheEntry>();

/**
 * 이미지 다운스케일 캐시 엔트리
 */
interface ImageDownscaleCacheEntry {
  canvas: HTMLCanvasElement;
  zoomLevel: number;
  renderScale: number;
  imageHash: string | null;
  lastUsed: number;
}

/**
 * 이미지 다운스케일 캐시 (객체 ID별, 최대 3개)
 */
const imageDownscaleCache = new Map<string, ImageDownscaleCacheEntry[]>();

/**
 * 객체 속성 해시 생성 (캐시 키용)
 */
function getOutGlowHash(obj: CanvasObject, w: number, h: number, scale: number, zoomLevel: number): string {
  return `${obj.id}_${obj.x}_${obj.y}_${obj.width}_${obj.height}_${obj.rotation}_${obj.outGlowSize}_${obj.outGlowColor}_${obj.outGlowOpacity}_${obj.outGlowSpread}_${w}_${h}_${scale}_${zoomLevel}`;
}

/**
 * 블러 캐시 키 생성
 */
function getBlurHash(obj: CanvasObject, w: number, h: number): string {
  const imageHash = (obj as any).imageHash || obj.id;
  const blurRadius = obj.blurRadius || 0;
  return `${imageHash}_${blurRadius}_${w}_${h}`;
}

/**
 * 클리핑 경로 생성 (회전 고려, 테두리 영역 제외)
 */
function createClipPath(ctx: CanvasRenderingContext2D, maskObj: CanvasObject) {
  const maskRot = maskObj.rotation || 0;
  const maskHasRot = Math.abs(maskRot) > 1e-6;
  
  // 테두리 두께에 따른 클리핑 경로 축소 계산
  const borderWidth = maskObj.borderWidth || 0;
  const borderPosition = maskObj.borderPosition || 'center';
  let insetX = 0;
  let insetY = 0;
  let insetRadius = 0;
  
  if (borderWidth > 0) {
    if (borderPosition === 'outside') {
      // 테두리가 바깥에 있으므로 클리핑 경로는 본체 영역 그대로
      insetX = 0;
      insetY = 0;
      insetRadius = 0;
    } else if (borderPosition === 'center') {
      // 테두리의 절반이 내부로 들어오므로 클리핑 경로를 절반만큼 축소
      insetX = borderWidth / 2;
      insetY = borderWidth / 2;
      insetRadius = borderWidth / 2;
    } else {
      // inside: 테두리 전체가 내부에 있으므로 클리핑 경로를 전체 두께만큼 축소
      insetX = borderWidth;
      insetY = borderWidth;
      insetRadius = borderWidth;
    }
  }
  
  if (maskHasRot) {
    const mcx = maskObj.x + maskObj.width / 2;
    const mcy = maskObj.y + maskObj.height / 2;
    ctx.translate(mcx, mcy);
    ctx.rotate(maskRot);
    ctx.translate(-mcx, -mcy);
  }
  
  if (maskObj.type === 'rectangle') {
    const clipX = maskObj.x + insetX;
    const clipY = maskObj.y + insetY;
    const clipW = Math.max(0, maskObj.width - insetX * 2);
    const clipH = Math.max(0, maskObj.height - insetY * 2);
    
    if (clipW <= 0 || clipH <= 0) return;
    
    // 원본 반경을 원본 크기에 대한 비율로 계산한 후, 클리핑 경로 크기에 맞게 비례 조정
    const origW = maskObj.width;
    const origH = maskObj.height;
    const scaleW = origW > 0 ? clipW / origW : 1;
    const scaleH = origH > 0 ? clipH / origH : 1;
    // 반경은 더 작은 축에 맞춰 스케일링 (둥근 모서리가 균형을 유지하도록)
    const scaleRadius = Math.min(scaleW, scaleH);
    
    let rTL = Math.max(0, (maskObj.borderRadiusTopLeft || 0) * scaleRadius - insetRadius);
    let rTR = Math.max(0, (maskObj.borderRadiusTopRight || 0) * scaleRadius - insetRadius);
    let rBL = Math.max(0, (maskObj.borderRadiusBottomLeft || 0) * scaleRadius - insetRadius);
    let rBR = Math.max(0, (maskObj.borderRadiusBottomRight || 0) * scaleRadius - insetRadius);
    
    // 모서리 반경 정규화: 인접 반경 합이 변 길이를 넘지 않도록 비례 스케일
    const sumTop = rTL + rTR;
    const sumBottom = rBL + rBR;
    const sumLeft = rTL + rBL;
    const sumRight = rTR + rBR;
    const k = Math.min(
      1,
      sumTop > 0 ? clipW / sumTop : 1,
      sumBottom > 0 ? clipW / sumBottom : 1,
      sumLeft > 0 ? clipH / sumLeft : 1,
      sumRight > 0 ? clipH / sumRight : 1
    );
    if (k < 1) {
      rTL *= k; rTR *= k; rBL *= k; rBR *= k;
    }
    
    const hasRadius = (rTL + rTR + rBL + rBR) > 0;
    
    if (hasRadius) {
      // 둥근 사각형 클리핑 경로
      drawRoundedRect(ctx, clipX, clipY, clipW, clipH, rTL, rTR, rBL, rBR);
    } else {
      // 일반 사각형 클리핑 경로
      ctx.rect(clipX, clipY, clipW, clipH);
    }
  } else if (maskObj.type === 'circle') {
    const cx = maskObj.x + maskObj.width / 2;
    const cy = maskObj.y + maskObj.height / 2;
    const rx = Math.max(0, maskObj.width / 2 - insetX);
    const ry = Math.max(0, maskObj.height / 2 - insetY);
    if (rx > 0 && ry > 0) {
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.closePath();
    }
  } else {
    // image, text 등은 직사각형
    const clipX = maskObj.x + insetX;
    const clipY = maskObj.y + insetY;
    const clipW = Math.max(0, maskObj.width - insetX * 2);
    const clipH = Math.max(0, maskObj.height - insetY * 2);
    if (clipW > 0 && clipH > 0) {
      ctx.rect(clipX, clipY, clipW, clipH);
    }
  }
  
  if (maskHasRot) {
    const mcx = maskObj.x + maskObj.width / 2;
    const mcy = maskObj.y + maskObj.height / 2;
    ctx.translate(mcx, mcy);
    ctx.rotate(-maskRot);
    ctx.translate(-mcx, -mcy);
  }
}

/**
 * 아웃글로우 렌더링 (성능 최적화: viewport culling, 회전 캐싱, 다운스케일링, 캐싱)
 */
function renderOutGlow(
  ctx: CanvasRenderingContext2D,
  obj: CanvasObject,
  viewLeft: number,
  viewTop: number,
  viewRight: number,
  viewBottom: number,
  scale: number,
  isInteracting: boolean = false
) {
  if (!obj.outGlowEnabled) return;
  
  // 성능 최적화: 이동/리사이즈/회전 중에는 아웃글로우 렌더링 스킵
  if (isInteracting) return;
  
  const size = obj.outGlowSize ?? 10;
  if (size <= 0) return;
  
  const baseOpacity = (obj.outGlowOpacity ?? 100) / 100;
  const color = obj.outGlowColor || '#000000';
  const spreadPct = Math.max(0, Math.min(100, obj.outGlowSpread ?? 0)) / 100;
  
  // 텍스트 객체의 경우 실제 렌더링 크기(오프스크린 캔버스 확장)를 반영
  let objWidth = obj.width;
  let objHeight = obj.height;
  let objX = obj.x;
  let objY = obj.y;
  if (obj.type === 'text') {
    // 확장된 크기의 중심점이 원본 크기의 중심점과 일치하도록 위치 조정
    const expandedWidth = objWidth * 1.4;
    const expandedHeight = objHeight * 1.4;
    objX = obj.x - (expandedWidth - objWidth) / 2;
    objY = obj.y - (expandedHeight - objHeight) / 2;
    objWidth = expandedWidth;
    objHeight = expandedHeight;
  }
  
  const objBounds = { x: objX, y: objY, width: objWidth, height: objHeight };
  
  // 회전 후 바운딩 박스 계산 (캐싱된 회전값 사용)
  const rot = obj.rotation || 0;
  const { cos: cosA, sin: sinA } = getAbsRotationValues(rot);
  const rotatedW = objBounds.width * cosA + objBounds.height * sinA;
  const rotatedH = objBounds.width * sinA + objBounds.height * cosA;
  
  // 블러 꼬리와 가장자리 노이즈 방지를 위해 충분한 패딩 확보
  const padding = Math.ceil(size * 3);
  const w = Math.max(1, Math.ceil(rotatedW + padding * 2));
  const h = Math.max(1, Math.ceil(rotatedH + padding * 2));
  
  // Viewport culling: 아웃글로우 영역이 viewport와 교차하는지 확인
  const glowX = objBounds.x - padding - (rotatedW - objBounds.width) / 2;
  const glowY = objBounds.y - padding - (rotatedH - objBounds.height) / 2;
  if (!intersectsRect(glowX, glowY, w, h, viewLeft, viewTop, viewRight, viewBottom)) {
    return; // viewport 밖이면 렌더링 스킵
  }
  
  // 줌 레벨 기반 다운스케일링 (이미지와 동일한 방식)
  const zoomLevel = getZoomLevel(scale);
  const renderScale = getRenderScale(zoomLevel);
  
  // 최적화 4: 캔버스 캐싱 (다중 캐시 방식)
  const cacheHash = getOutGlowHash(obj, w, h, renderScale, zoomLevel);
  const cacheKey = obj.id;
  let caches = outGlowCache.get(cacheKey) || [];
  
  // 기존 캐시에서 해당 줌 레벨 찾기
  const cached = caches.find(c => c.zoomLevel === zoomLevel && c.hash === cacheHash);
  if (cached) {
    // 캐시 히트: lastUsed 업데이트 및 재사용
    cached.lastUsed = Date.now();
    ctx.save();
    if (cached.renderScale < 1) {
      // 다운스케일링된 경우 업스케일하여 그리기
      ctx.drawImage(cached.canvas, cached.glowX, cached.glowY, cached.w / cached.renderScale, cached.h / cached.renderScale);
    } else {
      ctx.drawImage(cached.canvas, cached.glowX, cached.glowY);
    }
    ctx.restore();
    return;
  }
  
  // 1) 마스크 캔버스: 원본 모양을 불투명 흰색으로 채움 (원본 크기로 생성)
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = w;
  maskCanvas.height = h;
  const m = maskCanvas.getContext('2d');
  if (!m) return;
  
  // 회전된 BB 중앙에 원본 도형을 배치하기 위한 오프셋
  // 텍스트 객체의 경우 확장된 캔버스 중앙에 원본 크기 텍스트를 배치
  const originalWidth = obj.type === 'text' ? obj.width : objBounds.width;
  const originalHeight = obj.type === 'text' ? obj.height : objBounds.height;
  const ox = padding + (rotatedW - originalWidth) / 2;
  const oy = padding + (rotatedH - originalHeight) / 2;
  
  const applyRotation = (gctx: CanvasRenderingContext2D) => {
    if (rot) {
      const cx = ox + originalWidth / 2;
      const cy = oy + originalHeight / 2;
      gctx.translate(cx, cy);
      gctx.rotate(rot);
      gctx.translate(-cx, -cy);
    }
  };
  
  m.save();
  applyRotation(m);
  m.fillStyle = '#ffffff';
  
  if (obj.type === 'rectangle') {
    const rTL = obj.borderRadiusTopLeft || 0;
    const rTR = obj.borderRadiusTopRight || 0;
    const rBL = obj.borderRadiusBottomLeft || 0;
    const rBR = obj.borderRadiusBottomRight || 0;
    const hasRadius = (rTL + rTR + rBL + rBR) > 0;
    
    if (hasRadius) {
      drawRoundedRect(m, ox, oy, objBounds.width, objBounds.height, rTL, rTR, rBL, rBR);
      m.fill();
    } else {
      m.fillRect(ox, oy, objBounds.width, objBounds.height);
    }
  } else if (obj.type === 'circle') {
    m.beginPath();
    const cx = ox + objBounds.width / 2;
    const cy = oy + objBounds.height / 2;
    const rx = objBounds.width / 2;
    const ry = objBounds.height / 2;
    if (m.ellipse) {
      m.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    } else {
      m.arc(cx, cy, Math.min(rx, ry), 0, Math.PI * 2);
    }
    m.fill();
  } else if (obj.type === 'image') {
    m.fillRect(ox, oy, objBounds.width, objBounds.height);
  } else if (obj.type === 'text' && obj.text) {
    const fontStyle = obj.fontStyle || 'normal';
    const fontWeight = obj.fontWeight || 'normal';
    const fontSize = obj.fontSize || 16;
    const fontFamily = obj.fontFamily || 'PretendardJP';
    // 폰트 패밀리 이름을 항상 따옴표로 감싸기
    const quotedFontFamily = `"${fontFamily}"`;
    m.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
    m.textBaseline = 'alphabetic';
    const lines = obj.text.split('\n');
    const lineHeight = fontSize * 1.2;
    const measure = (line: string) => m.measureText(line).width;
    const alignedX = (line: string) => {
      const tw = measure(line);
      if (obj.textAlign === 'center') return ox + (originalWidth - tw) / 2;
      if (obj.textAlign === 'right') return ox + (originalWidth - tw);
      return ox;
    };
    lines.forEach((line, i) => {
      m.fillText(line, alignedX(line), oy + fontSize + lineHeight * i);
    });
  }
  m.restore();
  
  // 2) 블러: filter로 마스크를 흐림
  const blurCanvas = document.createElement('canvas');
  blurCanvas.width = w;
  blurCanvas.height = h;
  const bc = blurCanvas.getContext('2d');
  if (!bc) return;
  bc.filter = `blur(${size}px)`;
  bc.drawImage(maskCanvas, 0, 0);
  bc.filter = 'none';
  
  // 다운스케일링 적용 (블러 후)
  let finalCanvas: HTMLCanvasElement = blurCanvas;
  let finalW = w;
  let finalH = h;
  
  if (renderScale < 1) {
    // 다운스케일링된 캔버스 생성
    finalW = Math.ceil(w * renderScale);
    finalH = Math.ceil(h * renderScale);
    finalCanvas = document.createElement('canvas');
    finalCanvas.width = finalW;
    finalCanvas.height = finalH;
    const fc = finalCanvas.getContext('2d');
    if (!fc) return;
    fc.drawImage(blurCanvas, 0, 0, w, h, 0, 0, finalW, finalH);
  }
  
  // 3) 스프레드 알파 재매핑 (최적화 5: spreadPct가 0이면 스킵)
  const processCanvas = finalCanvas.getContext('2d');
  if (!processCanvas) return;
  
  if (spreadPct > 0) {
    const img = processCanvas.getImageData(0, 0, finalW, finalH);
    const data = img.data;
    const threshold = 255 * (1 - spreadPct);
    const epsilon = 1;
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      let na = a < threshold ? (a / (threshold || 1)) * 255 : 255;
      na = Math.max(0, Math.min(255, na * baseOpacity));
      if (na < epsilon) na = 0;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = na;
    }
    processCanvas.putImageData(img, 0, 0);
  } else {
    // 스프레드가 0이면 투명도만 적용
    const img = processCanvas.getImageData(0, 0, finalW, finalH);
    const data = img.data;
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3];
      const na = Math.max(0, Math.min(255, a * baseOpacity));
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = na;
    }
    processCanvas.putImageData(img, 0, 0);
  }
  
  // 4) 색 적용: destination-in으로 알파 입힘
  const colorCanvas = document.createElement('canvas');
  colorCanvas.width = finalW;
  colorCanvas.height = finalH;
  const cc = colorCanvas.getContext('2d');
  if (!cc) return;
  cc.fillStyle = color;
  cc.fillRect(0, 0, finalW, finalH);
  cc.globalCompositeOperation = 'destination-in';
  cc.drawImage(finalCanvas, 0, 0);
  cc.globalCompositeOperation = 'source-over';
  
  // 5) 메인 캔버스에 합성 (객체 뒤)
  ctx.save();
  if (renderScale < 1) {
    // 다운스케일링된 경우 업스케일하여 그리기
    ctx.drawImage(colorCanvas, glowX, glowY, w, h);
  } else {
    ctx.drawImage(colorCanvas, glowX, glowY);
  }
  ctx.restore();
  
  // 캐시 저장 (LRU 방식, 최대 3개)
  const newEntry: OutGlowCacheEntry = {
    canvas: colorCanvas,
    glowX,
    glowY,
    hash: cacheHash,
    zoomLevel,
    renderScale,
    w: finalW,
    h: finalH,
    lastUsed: Date.now()
  };
  
  // 최대 3개까지만 유지 (LRU 방식)
  if (caches.length >= 3) {
    // 가장 오래된 캐시 제거
    caches.sort((a, b) => a.lastUsed - b.lastUsed);
    caches = caches.slice(1); // 가장 오래된 것 제거
  }
  
  caches.push(newEntry);
  outGlowCache.set(cacheKey, caches);
}

/**
 * 줌 레벨 범위 구분
 * 0.1 ~ 0.5: Level 0 (30% 다운스케일)
 * 0.5 ~ 1.0: Level 1 (60% 다운스케일)
 * 1.0 ~ 8.0: Level 2 (100% 원본)
 */
function getZoomLevel(scale: number): number {
  if (scale < 0.5) return 0;
  if (scale < 1.0) return 1;
  return 2;
}

/**
 * 줌 레벨에 따른 렌더링 스케일 반환
 */
function getRenderScale(zoomLevel: number): number {
  switch (zoomLevel) {
    case 0: return 0.3;  // 30% 다운스케일
    case 1: return 0.5;  // 50% 다운스케일
    default: return 1.0; // 100% 원본
  }
}

/**
 * 이미지 다운스케일 캐시 관리 (LRU 방식, 최대 3개)
 */
function getOrCreateDownscaledImage(
  obj: CanvasObject,
  zoomLevel: number,
  renderScale: number,
  targetWidth: number,
  targetHeight: number
): HTMLCanvasElement {
  if (!obj.image) {
    throw new Error('Image is required');
  }

  const cacheKey = obj.id;
  const imageHash = (obj as any).imageHash || null;
  let caches = imageDownscaleCache.get(cacheKey) || [];
  
  // 기존 캐시에서 해당 줌 레벨과 이미지 해시가 일치하는 것 찾기
  const existing = caches.find(c => c.zoomLevel === zoomLevel && c.imageHash === imageHash);
  if (existing) {
    // 캐시 히트: lastUsed 업데이트
    existing.lastUsed = Date.now();
    return existing.canvas;
  }
  
  // 캐시 미스: 새로 생성
  const downscaledCanvas = document.createElement('canvas');
  const scaledWidth = Math.ceil(targetWidth * renderScale);
  const scaledHeight = Math.ceil(targetHeight * renderScale);
  downscaledCanvas.width = scaledWidth;
  downscaledCanvas.height = scaledHeight;
  
  const downscaledCtx = downscaledCanvas.getContext('2d');
  if (!downscaledCtx) {
    throw new Error('Failed to create canvas context');
  }
  
  downscaledCtx.imageSmoothingEnabled = true;
  downscaledCtx.imageSmoothingQuality = 'low';
  
  // 원본 이미지를 다운스케일하여 그리기
  downscaledCtx.drawImage(
    obj.image,
    0, 0, obj.image.width, obj.image.height,
    0, 0, scaledWidth, scaledHeight
  );
  
  // 새 캐시 엔트리 생성
  const newEntry: ImageDownscaleCacheEntry = {
    canvas: downscaledCanvas,
    zoomLevel,
    renderScale,
    imageHash,
    lastUsed: Date.now()
  };
  
  // 최대 3개까지만 유지 (LRU 방식)
  if (caches.length >= 3) {
    // 가장 오래된 캐시 제거
    caches.sort((a, b) => a.lastUsed - b.lastUsed);
    caches = caches.slice(1); // 가장 오래된 것 제거
  }
  
  caches.push(newEntry);
  imageDownscaleCache.set(cacheKey, caches);
  
  return downscaledCanvas;
}

/**
 * 객체 본체만 렌더링 (공통 함수, 테두리 제외)
 */
function renderObjectBody(
  ctx: CanvasRenderingContext2D,
  obj: CanvasObject,
  scale: number = 1.0,
  isInteracting: boolean = false,
  interactingObjectIds?: Set<string>
) {
  // 원본 좌표와 크기
  const origX = Math.round(obj.x);
  const origY = Math.round(obj.y);
  const origW = Math.round(obj.width);
  const origH = Math.round(obj.height);
  
  // 테두리 위치에 따른 객체 본체 크기 조정 (안티앨리어싱 문제 해결)
  // 텍스트는 제외하고, rectangle, circle, image만 적용
  let x = origX;
  let y = origY;
  let w = origW;
  let h = origH;
  let radiusScale = 1; // 둥근 모서리 반경 스케일
  
  const borderWidth = obj.borderWidth || 0;
  const borderPosition = obj.borderPosition || 'center';
  const hasBorder = borderWidth > 0 && obj.type !== 'text';
  
  if (hasBorder && borderPosition !== 'center') {
    const halfBorder = borderWidth / 2;
    
    if (borderPosition === 'outside') {
      // 외부 테두리: 객체 본체를 borderWidth/2만큼 확장
      x = origX - halfBorder;
      y = origY - halfBorder;
      w = origW + borderWidth;
      h = origH + borderWidth;
      // 둥근 모서리 반경도 비례 확장
      radiusScale = (origW + borderWidth) / origW; // 너비 기준 스케일
    } else if (borderPosition === 'inside') {
      // 내부 테두리: 객체 본체를 borderWidth/2만큼 축소
      x = origX + halfBorder;
      y = origY + halfBorder;
      w = Math.max(0, origW - borderWidth);
      h = Math.max(0, origH - borderWidth);
      // 둥근 모서리 반경도 비례 축소
      if (origW > 0) {
        radiusScale = (origW - borderWidth) / origW; // 너비 기준 스케일
      }
    }
  }
  
  const rot = obj.rotation || 0;
  const hasRot = Math.abs(rot) > 1e-6;
  const cx = x + w / 2;
  const cy = y + h / 2;

  if (hasRot) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    ctx.translate(-cx, -cy);
  }

  // 객체 본체 렌더링
  if (obj.type === 'rectangle') {
    ctx.fillStyle = obj.backgroundColor || '#74c0fc';
    
    // 둥근 모서리 체크 (성능 최적화: 한 번만 계산)
    let rTL = obj.borderRadiusTopLeft || 0;
    let rTR = obj.borderRadiusTopRight || 0;
    let rBL = obj.borderRadiusBottomLeft || 0;
    let rBR = obj.borderRadiusBottomRight || 0;
    
    // 테두리 위치에 따라 반경 스케일 조정
    if (hasBorder && borderPosition !== 'center') {
      rTL = rTL * radiusScale;
      rTR = rTR * radiusScale;
      rBL = rBL * radiusScale;
      rBR = rBR * radiusScale;
    }
    
    const hasRadius = (rTL + rTR + rBL + rBR) > 0;
    
    if (hasRadius) {
      // 둥근 사각형
      drawRoundedRect(ctx, x, y, w, h, rTL, rTR, rBL, rBR);
      ctx.fill();
    } else {
      // 일반 사각형 (최적화: fillRect가 가장 빠름)
      ctx.fillRect(x, y, w, h);
    }
  } else if (obj.type === 'circle') {
    const rcx = Math.round(cx);
    const rcy = Math.round(cy);
    const rx = Math.max(0, Math.round(w / 2));
    const ry = Math.max(0, Math.round(h / 2));
    ctx.beginPath();
    ctx.ellipse(rcx, rcy, rx, ry, 0, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = obj.backgroundColor || '#ffd43b';
    ctx.fill();
  } else if (obj.type === 'image' && obj.image) {
    // 이미지 블러 효과 적용
    const blurEnabled = obj.blurEnabled && obj.blurRadius && obj.blurRadius > 0;
    const isThisObjectInteracting = interactingObjectIds ? interactingObjectIds.has(obj.id) : isInteracting;
    
    if (blurEnabled && !isThisObjectInteracting) {
      // 블러 캐싱: 캐시 키 생성
      const cacheKey = getBlurHash(obj, w, h);
      const cached = blurCache.get(cacheKey);
      
      if (cached && cached.hash === cacheKey) {
        // 캐시된 블러 이미지 재사용
        ctx.drawImage(cached.canvas, x, y);
      } else {
        // 블러 적용: 임시 캔버스에 그린 후 필터 적용
        const blurCanvas = document.createElement('canvas');
        blurCanvas.width = w;
        blurCanvas.height = h;
        const blurCtx = blurCanvas.getContext('2d');
        if (blurCtx) {
          blurCtx.imageSmoothingEnabled = true;
          blurCtx.imageSmoothingQuality = 'low'; // 렌더링 시에는 low 품질
          blurCtx.filter = `blur(${obj.blurRadius}px)`;
          blurCtx.drawImage(obj.image, 0, 0, w, h);
          blurCtx.filter = 'none';
          ctx.drawImage(blurCanvas, x, y);
          
          // 캐시 저장 (최대 50개까지만 유지)
          if (blurCache.size > 50) {
            // 가장 오래된 항목 제거 (FIFO)
            const firstKey = blurCache.keys().next().value;
            if (firstKey) {
              blurCache.delete(firstKey);
            }
          }
          
          blurCache.set(cacheKey, {
            canvas: blurCanvas,
            hash: cacheKey,
            w,
            h
          });
        } else {
          // 컨텍스트 생성 실패 시 일반 렌더링
          ctx.drawImage(obj.image, x, y, w, h);
        }
      }
    } else {
      // 블러 없이 일반 렌더링: 줌 레벨에 따른 다운스케일 캐싱 적용
      const zoomLevel = getZoomLevel(scale);
      const renderScale = getRenderScale(zoomLevel);
      
      // 상호작용 중이거나 원본 해상도가 필요하면 원본 사용
      if (isThisObjectInteracting || renderScale >= 1.0) {
        ctx.drawImage(obj.image, x, y, w, h);
      } else {
        // 다운스케일 캐시 사용
        try {
          const downscaledCanvas = getOrCreateDownscaledImage(
            obj,
            zoomLevel,
            renderScale,
            w,
            h
          );
          
          // 다운스케일된 이미지를 원본 크기로 업스케일하여 그리기
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'low';
          ctx.drawImage(downscaledCanvas, x, y, w, h);
        } catch (error) {
          // 캐시 생성 실패 시 원본 사용
          console.warn('Failed to create downscaled image cache:', error);
          ctx.drawImage(obj.image, x, y, w, h);
        }
      }
    }
  } else if (obj.type === 'text' && obj.text) {
    // 텍스트는 원본 좌표와 크기 사용 (조정하지 않음)
    ctx.fillStyle = obj.color || '#000000';
    const fontStyle = obj.fontStyle || 'normal';
    const fontWeight = obj.fontWeight || 'normal';
    const fontSize = obj.fontSize || 16;
    const fontFamily = obj.fontFamily || 'PretendardJP';
    // 폰트 패밀리 이름을 항상 따옴표로 감싸기
    const quotedFontFamily = `"${fontFamily}"`;
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
    ctx.textBaseline = 'alphabetic';
    
    // 여러 줄 텍스트 처리
    const lines = obj.text.split('\n');
    const lineHeight = fontSize * 1.2;
    const measureLineWidth = (line: string) => ctx.measureText(line).width;
    const computeAlignedX = (line: string) => {
      const lineWidth = measureLineWidth(line);
      if (obj.textAlign === 'center') return origX + (origW - lineWidth) / 2;
      if (obj.textAlign === 'right') return origX + (origW - lineWidth);
      return origX; // left
    };
    
    lines.forEach((line, i) => {
      const textX = computeAlignedX(line);
      const textY = origY + fontSize + lineHeight * i;
      ctx.fillText(line, textX, textY);
    });
  }

  if (hasRot) {
    ctx.restore();
  }
}

/**
 * 객체 테두리만 렌더링
 */
function renderObjectBorder(ctx: CanvasRenderingContext2D, obj: CanvasObject) {
  if (!obj.borderWidth || obj.borderWidth <= 0) return;
  
  const x = Math.round(obj.x);
  const y = Math.round(obj.y);
  const w = Math.round(obj.width);
  const h = Math.round(obj.height);
  
  const rot = obj.rotation || 0;
  const hasRot = Math.abs(rot) > 1e-6;
  const cx = x + w / 2;
  const cy = y + h / 2;

  if (hasRot) {
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    ctx.translate(-cx, -cy);
  }

  // 기본값 사용 (안전장치)
  const borderColor = obj.borderColor || '#000000';
  const borderPos = obj.borderPosition || 'center';
  const borderWidth = obj.borderWidth;
  
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = borderWidth;
  
  if (borderPos === 'outside') {
    // 외부 테두리: evenodd fill 방식
    if (obj.type === 'rectangle') {
      const rTL = obj.borderRadiusTopLeft || 0;
      const rTR = obj.borderRadiusTopRight || 0;
      const rBL = obj.borderRadiusBottomLeft || 0;
      const rBR = obj.borderRadiusBottomRight || 0;
      const hasRadius = (rTL + rTR + rBL + rBR) > 0;
      
      if (hasRadius) {
        // 둥근 링: 외부/내부 둥근 사각형 경로 생성
        const outer = createRoundedRectPath(
          x - borderWidth,
          y - borderWidth,
          w + borderWidth * 2,
          h + borderWidth * 2,
          rTL + borderWidth,
          rTR + borderWidth,
          rBL + borderWidth,
          rBR + borderWidth
        );
        const inner = createRoundedRectPath(x, y, w, h, rTL, rTR, rBL, rBR);
        const ring = new Path2D(outer as any);
        (ring as any).addPath(inner);
        ctx.fillStyle = borderColor;
        (ctx as any).fill(ring, 'evenodd');
      } else {
        // 일반 사각 링
        const outerRect = new Path2D();
        outerRect.rect(
          x - borderWidth,
          y - borderWidth,
          w + borderWidth * 2,
          h + borderWidth * 2
        );
        const innerRect = new Path2D();
        innerRect.rect(x, y, w, h);
        const ring = new Path2D(outerRect as any);
        (ring as any).addPath(innerRect);
        ctx.fillStyle = borderColor;
        (ctx as any).fill(ring, 'evenodd');
      }
    } else if (obj.type === 'circle') {
      const rcx = Math.round(cx);
      const rcy = Math.round(cy);
      const outerRx = Math.max(0, Math.round(w / 2) + borderWidth);
      const outerRy = Math.max(0, Math.round(h / 2) + borderWidth);
      const innerRx = Math.max(0, Math.round(w / 2));
      const innerRy = Math.max(0, Math.round(h / 2));
      
      const outer = new Path2D();
      outer.ellipse(rcx, rcy, outerRx, outerRy, 0, 0, Math.PI * 2);
      const inner = new Path2D();
      inner.ellipse(rcx, rcy, innerRx, innerRy, 0, 0, Math.PI * 2);
      const ring = new Path2D(outer as any);
      (ring as any).addPath(inner);
      ctx.fillStyle = borderColor;
      (ctx as any).fill(ring, 'evenodd');
    } else if (obj.type === 'image') {
      // 이미지도 직사각형으로 처리
      const outerRect = new Path2D();
      outerRect.rect(
        x - borderWidth,
        y - borderWidth,
        w + borderWidth * 2,
        h + borderWidth * 2
      );
      const innerRect = new Path2D();
      innerRect.rect(x, y, w, h);
      const ring = new Path2D(outerRect as any);
      (ring as any).addPath(innerRect);
      ctx.fillStyle = borderColor;
      (ctx as any).fill(ring, 'evenodd');
    } else if (obj.type === 'text' && obj.text) {
      // 텍스트 외부 테두리: 8방향 strokeText 반복
      const fontStyle = obj.fontStyle || 'normal';
      const fontWeight = obj.fontWeight || 'normal';
      const fontSize = obj.fontSize || 16;
      const fontFamily = obj.fontFamily || 'PretendardJP';
      // 폰트 패밀리 이름을 항상 따옴표로 감싸기
      const quotedFontFamily = `"${fontFamily}"`;
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
      ctx.textBaseline = 'alphabetic';
      
      // 여러 줄 텍스트 처리
      const lines = obj.text.split('\n');
      const lineHeight = fontSize * 1.2;
      const measureLineWidth = (line: string) => ctx.measureText(line).width;
      const computeAlignedX = (line: string) => {
        const lineWidth = measureLineWidth(line);
        if (obj.textAlign === 'center') return x + (w - lineWidth) / 2;
        if (obj.textAlign === 'right') return x + (w - lineWidth);
        return x; // left
      };
      
      // 각 라인에 대해 외부 테두리 그리기
      lines.forEach((line, i) => {
        const textX = computeAlignedX(line);
        const textY = y + fontSize + lineHeight * i;
        
        // 8방향으로 strokeText 반복 (선명한 테두리)
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        for (let dx = -borderWidth; dx <= borderWidth; dx++) {
          for (let dy = -borderWidth; dy <= borderWidth; dy++) {
            if (dx !== 0 || dy !== 0) {
              ctx.strokeText(line, textX + dx, textY + dy);
            }
          }
        }
      });
    }
  } else {
    // 중앙/내부 테두리: stroke 방식
    const borderCoords = getBorderCoordinates(x, y, w, h, borderWidth, borderPos);
    const aligned = alignForStroke(borderCoords.x, borderCoords.y, borderCoords.width, borderCoords.height, borderWidth, borderPos);
    
    if (obj.type === 'rectangle') {
      const rTL = obj.borderRadiusTopLeft || 0;
      const rTR = obj.borderRadiusTopRight || 0;
      const rBL = obj.borderRadiusBottomLeft || 0;
      const rBR = obj.borderRadiusBottomRight || 0;
      const hasRadius = (rTL + rTR + rBL + rBR) > 0;
      
      if (hasRadius) {
        // 둥근 사각형 테두리: 보정된 반경 사용
        // 실제 테두리 영역 크기를 기준으로 반경 계산 (aligned 좌표 사용)
        ctx.beginPath();
        drawRoundedRect(
          ctx,
          aligned.x,
          aligned.y,
          aligned.width,
          aligned.height,
          adjustCornerRadiusForBorder(rTL, borderWidth, borderPos, w, h, aligned.width, aligned.height),
          adjustCornerRadiusForBorder(rTR, borderWidth, borderPos, w, h, aligned.width, aligned.height),
          adjustCornerRadiusForBorder(rBL, borderWidth, borderPos, w, h, aligned.width, aligned.height),
          adjustCornerRadiusForBorder(rBR, borderWidth, borderPos, w, h, aligned.width, aligned.height)
        );
        ctx.stroke();
      } else {
        // 일반 사각형 테두리
        ctx.strokeRect(aligned.x, aligned.y, aligned.width, aligned.height);
      }
    } else if (obj.type === 'image') {
      // 이미지는 직사각형으로 처리
      ctx.strokeRect(aligned.x, aligned.y, aligned.width, aligned.height);
    } else if (obj.type === 'circle') {
      const strokeCx = aligned.x + aligned.width / 2;
      const strokeCy = aligned.y + aligned.height / 2;
      const strokeRx = Math.max(0, aligned.width / 2);
      const strokeRy = Math.max(0, aligned.height / 2);
      ctx.beginPath();
      ctx.ellipse(strokeCx, strokeCy, strokeRx, strokeRy, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (obj.type === 'text' && obj.text) {
      // 텍스트 중앙/내부 테두리: strokeText 사용
      const fontStyle = obj.fontStyle || 'normal';
      const fontWeight = obj.fontWeight || 'normal';
      const fontSize = obj.fontSize || 16;
      const fontFamily = obj.fontFamily || 'PretendardJP';
      // 폰트 패밀리 이름을 항상 따옴표로 감싸기
      const quotedFontFamily = `"${fontFamily}"`;
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${quotedFontFamily}`;
      ctx.textBaseline = 'alphabetic';
      
      // 여러 줄 텍스트 처리
      const lines = obj.text.split('\n');
      const lineHeight = fontSize * 1.2;
      const measureLineWidth = (line: string) => ctx.measureText(line).width;
      const computeAlignedX = (line: string) => {
        const lineWidth = measureLineWidth(line);
        if (obj.textAlign === 'center') return x + (w - lineWidth) / 2;
        if (obj.textAlign === 'right') return x + (w - lineWidth);
        return x; // left
      };
      
      // 테두리 스타일 설정
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = borderWidth;
      
      // 각 라인에 대해 테두리 그리기
      lines.forEach((line, i) => {
        const textX = computeAlignedX(line);
        const textY = y + fontSize + lineHeight * i;
        ctx.strokeText(line, textX, textY);
      });
    }
  }

  if (hasRot) {
    ctx.restore();
  }
}

/**
 * 단일 객체 렌더링 (아웃글로우, 테두리 포함)
 */
function renderSingleObject(
  ctx: CanvasRenderingContext2D,
  obj: CanvasObject,
  viewLeft: number,
  viewTop: number,
  viewRight: number,
  viewBottom: number,
  scale: number,
  isInteracting: boolean = false,
  interactingObjectIds?: Set<string>
) {
  // 레이어 투명도 (최종 합성 시 한 번만 적용)
  const layerAlpha = Math.max(0, Math.min(1, (obj.opacity ?? 100) / 100));

  // 아웃글로우는 가장 먼저 렌더링 (객체 뒤) - 레이어 알파를 한 번만 적용
  ctx.save();
  if (layerAlpha !== 1) ctx.globalAlpha *= layerAlpha;
  if (obj.outGlowEnabled) {
    renderOutGlow(ctx, obj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting);
  }
  ctx.restore();
  
  // 본체+테두리 합성: 오프스크린에 미리 그린 후 한 번만 합성하여
  // 채움/스트로크 겹침 시 알파 누적(이중 칠) 현상을 방지
  const rot = obj.rotation || 0;
  const hasRot = Math.abs(rot) > 1e-6;
  const cx = obj.x + obj.width / 2;
  const cy = obj.y + obj.height / 2;
  
  // 회전된 객체의 실제 경계 계산 (회전 변환 고려)
  let drawW = Math.ceil(obj.width);
  let drawH = Math.ceil(obj.height);
  if (hasRot) {
    // 회전된 사각형의 4개 모서리를 회전 변환하여 실제 경계 계산
    const w = obj.width;
    const h = obj.height;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    
    // 중심 기준 모서리 좌표 (회전 전)
    const corners = [
      { x: -w / 2, y: -h / 2 },
      { x: w / 2, y: -h / 2 },
      { x: w / 2, y: h / 2 },
      { x: -w / 2, y: h / 2 }
    ];
    
    // 회전 변환 적용
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    for (const corner of corners) {
      const rx = corner.x * cos - corner.y * sin;
      const ry = corner.x * sin + corner.y * cos;
      minX = Math.min(minX, rx);
      maxX = Math.max(maxX, rx);
      minY = Math.min(minY, ry);
      maxY = Math.max(maxY, ry);
    }
    
    drawW = Math.ceil(maxX - minX);
    drawH = Math.ceil(maxY - minY);
  }
  
  // 텍스트 객체의 경우 오프스크린 캔버스를 상하좌우 각 20%씩 확장 (세리프 폰트 대응)
  // 좌우 각 20% + 상하 각 20% = 전체 크기의 1.4배
  if (obj.type === 'text') {
    drawW = Math.ceil(drawW * 1.4);
    drawH = Math.ceil(drawH * 1.4);
  }
  
  // 테두리 여유분: 위치별로 필요한 패딩 계산
  const bw = obj.borderWidth || 0;
  const bp = obj.borderPosition || 'center';
  let extra = 0;
  if (bp === 'outside') {
    // 전부 바깥으로 나가므로 전체 두께만큼 확보
    // 회전 시 테두리도 회전되므로 대각선 방향 고려
    extra = Math.ceil(bw * Math.sqrt(2));
  } else if (bp === 'center') {
    // 절반은 바깥으로, 회전 고려
    extra = Math.ceil((bw / 2) * Math.sqrt(2));
  } else {
    // inside는 외부로 나가지 않지만, 회전 시 약간의 여유 필요
    extra = 1;
  }
  // 선 끝/조인 확장을 고려한 여유 추가
  extra = Math.max(1, extra + 2);
  const offW = Math.max(1, drawW + extra * 2);
  const offH = Math.max(1, drawH + extra * 2);
  const off = document.createElement('canvas');
  off.width = offW;
  off.height = offH;
  const octx = off.getContext('2d');
  if (octx) {
    // 원본 좌표로 맞추기 위한 평행이동
    const left = Math.round(cx - offW / 2);
    const top = Math.round(cy - offH / 2);
    octx.save();
    octx.translate(-left, -top);
    // 텍스트 외부 테두리 예외 처리 동일 유지
    const isTextWithOutsideBorder = obj.type === 'text' &&
      obj.borderWidth &&
      obj.borderWidth > 0 &&
      obj.borderPosition === 'outside';
    if (isTextWithOutsideBorder) {
      renderObjectBorder(octx, obj);
      renderObjectBody(octx, obj, scale, isInteracting, interactingObjectIds);
    } else {
      renderObjectBody(octx, obj, scale, isInteracting, interactingObjectIds);
      renderObjectBorder(octx, obj);
    }
    octx.restore();
    // 메인 캔버스에 레이어 알파와 블렌드 모드 적용하여 합성
    ctx.save();
    // 블렌드 모드 적용 (normal이 아닐 때만)
    const blendOp = getBlendModeOperation(obj.blendMode);
    if (blendOp) {
      ctx.globalCompositeOperation = blendOp;
    }
    // 투명도 적용
    if (layerAlpha !== 1) ctx.globalAlpha *= layerAlpha;
    ctx.drawImage(off, left, top);
    ctx.restore();
  }
}

/**
 * 마스크 객체 본체만 렌더링 (테두리 제외)
 * 주의: 이 함수는 이미 회전이 적용된 컨텍스트에서 호출되므로, 
 * renderObjectBody에 rotation을 0으로 설정한 객체를 전달하여 회전 중복 적용을 방지합니다.
 */
function renderMaskObjectBasicShape(ctx: CanvasRenderingContext2D, obj: CanvasObject, scale: number = 1.0) {
  // 회전 없이 객체 본체만 렌더링 (회전은 이미 컨텍스트에 적용됨)
  const tempObj = { ...obj, rotation: 0 };
  renderObjectBody(ctx, tempObj, scale);
}

/**
 * 마스크 객체 테두리만 렌더링
 * 주의: 이 함수는 이미 회전이 적용된 컨텍스트에서 호출되므로, 
 * renderObjectBorder에 rotation을 0으로 설정한 객체를 전달하여 회전 중복 적용을 방지합니다.
 */
function renderMaskObjectBorder(ctx: CanvasRenderingContext2D, obj: CanvasObject) {
  // 회전 없이 객체 테두리만 렌더링 (회전은 이미 컨텍스트에 적용됨)
  const tempObj = { ...obj, rotation: 0 };
  renderObjectBorder(ctx, tempObj);
}

export function renderObjects(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  objects: CanvasObject[],
  clipMaskMap?: ClipMaskMap,
  isInteracting: boolean = false,
  artboardsMap?: Map<string, Artboard>,
  interactingObjectIds?: Set<string>
) {
  const { viewLeft, viewTop, viewRight, viewBottom } = getViewportWorld(state, ctx.canvas as HTMLCanvasElement);
  const scale = state.scale;
  
  // 이미 렌더링된 마스크 그룹 추적 (중복 렌더링 방지)
  const renderedMaskGroups = new Set<string>();
  
  // zOrder 순서대로 렌더링
  for (const obj of objects) {
    const x = Math.round(obj.x);
    const y = Math.round(obj.y);
    const w = Math.round(obj.width);
    const h = Math.round(obj.height);
    if (!intersectsRect(x, y, w, h, viewLeft, viewTop, viewRight, viewBottom)) continue;
    
    // 클리핑된 객체는 마스크 그룹 내에서 처리되므로 스킵
    if (obj.isClipped && obj.clipMaskId) continue;
    
    // 마스크 객체를 만나면 마스크 그룹 전체를 렌더링
    if (clipMaskMap?.isMaskMap.has(obj.id)) {
      // 이미 렌더링된 마스크 그룹이면 스킵
      if (renderedMaskGroups.has(obj.id)) continue;
      
      const clippedObjects = clipMaskMap.clippedGroups.get(obj.id);
      if (!clippedObjects) {
        // 클리핑된 객체가 없는 마스크 객체는 일반 객체처럼 렌더링 (아트보드 클리핑 적용)
        if (obj.artboardId && artboardsMap) {
          const artboard = artboardsMap.get(obj.artboardId);
          if (artboard) {
            ctx.save();
            ctx.beginPath();
            ctx.rect(artboard.x, artboard.y, artboard.width, artboard.height);
            ctx.clip();
            renderSingleObject(ctx, obj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting, interactingObjectIds);
            ctx.restore();
          } else {
            renderSingleObject(ctx, obj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting, interactingObjectIds);
          }
        } else {
          renderSingleObject(ctx, obj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting, interactingObjectIds);
        }
        continue;
      }
      
      // 마스크 객체의 viewport 체크
      const mx = Math.round(obj.x);
      const my = Math.round(obj.y);
      const mw = Math.round(obj.width);
      const mh = Math.round(obj.height);
      if (!intersectsRect(mx, my, mw, mh, viewLeft, viewTop, viewRight, viewBottom)) continue;
      
      // 아트보드 클리핑 적용 (마스크 객체가 아트보드에 속한 경우)
      const maskArtboard = obj.artboardId && artboardsMap ? artboardsMap.get(obj.artboardId) : null;
      if (maskArtboard) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(maskArtboard.x, maskArtboard.y, maskArtboard.width, maskArtboard.height);
        ctx.clip();
      }
      
      // 마스크 객체의 투명도 적용
      const maskAlpha = Math.max(0, Math.min(1, (obj.opacity ?? 100) / 100));
      
      // 회전된 객체의 실제 경계 계산 (회전 변환 고려)
      const rot = obj.rotation || 0;
      const hasRot = Math.abs(rot) > 1e-6;
      const cx = obj.x + obj.width / 2;
      const cy = obj.y + obj.height / 2;
      
      let drawW = Math.ceil(obj.width);
      let drawH = Math.ceil(obj.height);
      if (hasRot) {
        const w = obj.width;
        const h = obj.height;
        const cos = Math.cos(rot);
        const sin = Math.sin(rot);
        
        const corners = [
          { x: -w / 2, y: -h / 2 },
          { x: w / 2, y: -h / 2 },
          { x: w / 2, y: h / 2 },
          { x: -w / 2, y: h / 2 }
        ];
        
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        for (const corner of corners) {
          const rx = corner.x * cos - corner.y * sin;
          const ry = corner.x * sin + corner.y * cos;
          minX = Math.min(minX, rx);
          maxX = Math.max(maxX, rx);
          minY = Math.min(minY, ry);
          maxY = Math.max(maxY, ry);
        }
        
        drawW = Math.ceil(maxX - minX);
        drawH = Math.ceil(maxY - minY);
      }
      
      // 오프스크린 크기 계산: 아웃글로우 + 테두리 여유분 포함
      const outGlowSize = obj.outGlowEnabled ? (obj.outGlowSize ?? 10) : 0;
      const outGlowPadding = outGlowSize > 0 ? Math.ceil(outGlowSize * 3) : 0;
      
      const maskBorderPos = obj.borderPosition || 'center';
      const maskBorderWidth = obj.borderWidth || 0;
      let borderExtra = 0;
      if (maskBorderWidth > 0) {
        if (maskBorderPos === 'outside') {
          borderExtra = Math.ceil(maskBorderWidth * Math.sqrt(2));
        } else if (maskBorderPos === 'center') {
          borderExtra = Math.ceil((maskBorderWidth / 2) * Math.sqrt(2));
        } else {
          borderExtra = 1;
        }
      }
      borderExtra = Math.max(1, borderExtra + 2);
      
      const extra = Math.max(outGlowPadding, borderExtra);
      const offW = Math.max(1, drawW + extra * 2);
      const offH = Math.max(1, drawH + extra * 2);
      const left = Math.round(cx - offW / 2);
      const top = Math.round(cy - offH / 2);
      
      // 메인 캔버스 크기
      const mainCanvas = ctx.canvas as HTMLCanvasElement;
      
      // 1. 마스크 아웃글로우를 먼저 메인 캔버스에 렌더링
      if (obj.outGlowEnabled) {
        ctx.save();
        if (maskAlpha !== 1) ctx.globalAlpha *= maskAlpha;
        const maskBlendOp = getBlendModeOperation(obj.blendMode);
        if (maskBlendOp) {
          ctx.globalCompositeOperation = maskBlendOp;
        }
        renderOutGlow(ctx, obj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting);
        ctx.restore();
      }
      
      // 2. 부모 비주얼(마스크 객체 본체) 생성
      const parentBase = document.createElement('canvas');
      parentBase.width = offW;
      parentBase.height = offH;
      const pctx = parentBase.getContext('2d');
      if (pctx) {
        pctx.save();
        pctx.translate(-left, -top);
        if (hasRot) {
          pctx.translate(cx, cy);
          pctx.rotate(rot);
          pctx.translate(-cx, -cy);
        }
        
        renderMaskObjectBasicShape(pctx, obj, scale);
        
        // 클리핑 레이어와 겹치는 부분 제거 (교집합 제외)
        // 단, 블렌드모드나 투명도가 있는 클리핑 레이어는 제외하지 않음 (블렌드 대상이 필요)
        if (clippedObjects.length > 0) {
          // 마스크 레이어의 회전을 역회전시켜 원래 좌표계로 복원 (클리핑 레이어는 원래 좌표계에서 그려야 함)
          if (hasRot) {
            pctx.translate(cx, cy);
            pctx.rotate(-rot);
            pctx.translate(-cx, -cy);
          }
          
          pctx.globalCompositeOperation = 'destination-out';
          pctx.fillStyle = '#000000';
          
          for (const clippedObj of clippedObjects) {
            // 블렌드모드나 투명도가 있는지 확인
            const hasBlendMode = getBlendModeOperation(clippedObj.blendMode) !== null;
            const hasOpacity = (clippedObj.opacity ?? 100) < 100;
            
            // 블렌드모드나 투명도가 있으면 교집합을 유지해야 하므로 제거하지 않음
            if (hasBlendMode || hasOpacity) {
              continue;
            }
            
            // 클리핑 레이어의 회전 고려
            const clippedRot = clippedObj.rotation || 0;
            const clippedHasRot = Math.abs(clippedRot) > 1e-6;
            
            if (clippedHasRot) {
              const clippedCx = clippedObj.x + clippedObj.width / 2;
              const clippedCy = clippedObj.y + clippedObj.height / 2;
              pctx.save();
              pctx.translate(clippedCx, clippedCy);
              pctx.rotate(clippedRot);
              pctx.translate(-clippedCx, -clippedCy);
            }
            
            // 클리핑 레이어의 정확한 경계 그리기
            if (clippedObj.type === 'rectangle') {
              const rTL = clippedObj.borderRadiusTopLeft || 0;
              const rTR = clippedObj.borderRadiusTopRight || 0;
              const rBL = clippedObj.borderRadiusBottomLeft || 0;
              const rBR = clippedObj.borderRadiusBottomRight || 0;
              const hasRadius = (rTL + rTR + rBL + rBR) > 0;
              
              if (hasRadius) {
                drawRoundedRect(pctx, clippedObj.x, clippedObj.y, clippedObj.width, clippedObj.height, rTL, rTR, rBL, rBR);
                pctx.fill();
              } else {
                pctx.fillRect(clippedObj.x, clippedObj.y, clippedObj.width, clippedObj.height);
              }
            } else if (clippedObj.type === 'circle') {
              pctx.beginPath();
              const cCx = clippedObj.x + clippedObj.width / 2;
              const cCy = clippedObj.y + clippedObj.height / 2;
              const cRx = clippedObj.width / 2;
              const cRy = clippedObj.height / 2;
              pctx.ellipse(cCx, cCy, cRx, cRy, 0, 0, Math.PI * 2);
              pctx.closePath();
              pctx.fill();
            } else {
              // image, text 등은 직사각형
              pctx.fillRect(clippedObj.x, clippedObj.y, clippedObj.width, clippedObj.height);
            }
            
            if (clippedHasRot) {
              pctx.restore();
            }
          }
          
          pctx.globalCompositeOperation = 'source-over';
          
          // 마스크 레이어의 회전을 다시 적용 (restore 전에 원래 상태로 복원)
          if (hasRot) {
            pctx.translate(cx, cy);
            pctx.rotate(rot);
            pctx.translate(-cx, -cy);
          }
        }
        
        pctx.restore();
      }
      
      // 3. 마스크 알파 생성 (클리핑 영역)
      // 테두리 위치에 따른 확대/축소를 마스크 객체 본체와 동일하게 적용
      const maskAlphaCanvas = document.createElement('canvas');
      maskAlphaCanvas.width = offW;
      maskAlphaCanvas.height = offH;
      const ma = maskAlphaCanvas.getContext('2d');
      if (ma) {
        ma.save();
        ma.translate(-left, -top);
        if (hasRot) {
          ma.translate(cx, cy);
          ma.rotate(rot);
          ma.translate(-cx, -cy);
        }
        
        // 원본 좌표와 크기
        const origX = obj.x;
        const origY = obj.y;
        const origW = obj.width;
        const origH = obj.height;
        
        // 테두리 위치에 따른 객체 본체 크기 조정 (renderObjectBody와 동일한 로직)
        let x = origX;
        let y = origY;
        let w = origW;
        let h = origH;
        let radiusScale = 1; // 둥근 모서리 반경 스케일
        
        const borderWidth = obj.borderWidth || 0;
        const borderPosition = obj.borderPosition || 'center';
        const hasBorder = borderWidth > 0 && obj.type !== 'text';
        
        if (hasBorder && borderPosition !== 'center') {
          const halfBorder = borderWidth / 2;
          
          if (borderPosition === 'outside') {
            // 외부 테두리: 객체 본체를 borderWidth/2만큼 확장
            x = origX - halfBorder;
            y = origY - halfBorder;
            w = origW + borderWidth;
            h = origH + borderWidth;
            // 둥근 모서리 반경도 비례 확장
            radiusScale = (origW + borderWidth) / origW; // 너비 기준 스케일
          } else if (borderPosition === 'inside') {
            // 내부 테두리: 객체 본체를 borderWidth/2만큼 축소
            x = origX + halfBorder;
            y = origY + halfBorder;
            w = Math.max(0, origW - borderWidth);
            h = Math.max(0, origH - borderWidth);
            // 둥근 모서리 반경도 비례 축소
            if (origW > 0) {
              radiusScale = (origW - borderWidth) / origW; // 너비 기준 스케일
            }
          }
        }
        
        ma.fillStyle = '#ffffff';
        if (obj.type === 'rectangle') {
          let rTL = obj.borderRadiusTopLeft || 0;
          let rTR = obj.borderRadiusTopRight || 0;
          let rBL = obj.borderRadiusBottomLeft || 0;
          let rBR = obj.borderRadiusBottomRight || 0;
          
          // 테두리 위치에 따라 반경 스케일 조정
          if (hasBorder && borderPosition !== 'center') {
            rTL = rTL * radiusScale;
            rTR = rTR * radiusScale;
            rBL = rBL * radiusScale;
            rBR = rBR * radiusScale;
          }
          
          const hasRadius = (rTL + rTR + rBL + rBR) > 0;
          if (hasRadius) {
            drawRoundedRect(ma, x, y, w, h, rTL, rTR, rBL, rBR);
          } else {
            ma.fillRect(x, y, w, h);
          }
        } else if (obj.type === 'circle') {
          ma.beginPath();
          const mcx = x + w / 2;
          const mcy = y + h / 2;
          const rx = w / 2;
          const ry = h / 2;
          ma.ellipse(mcx, mcy, rx, ry, 0, 0, Math.PI * 2);
          ma.closePath();
        } else {
          ma.fillRect(x, y, w, h);
        }
        ma.fill();
        
        ma.restore();
      }
      
      // 4. 백드롭: 현재까지 메인 캔버스에 그려진 내용을 마스크 영역으로 제한
      const backdrop = document.createElement('canvas');
      backdrop.width = offW;
      backdrop.height = offH;
      const bd = backdrop.getContext('2d');
      if (bd && ma) {
        // 현재까지의 메인 캔버스 내용을 캡처
        bd.drawImage(mainCanvas, left, top, offW, offH, 0, 0, offW, offH);
        // 마스크 영역 밖 제거
        bd.globalCompositeOperation = 'destination-in';
        bd.drawImage(maskAlphaCanvas, 0, 0);
        bd.globalCompositeOperation = 'source-over';
      }
      
      // 5. 통합 블렌드 캔버스 생성: 모든 클리핑된 객체를 하나의 캔버스에 합성
      const blended = document.createElement('canvas');
      blended.width = offW;
      blended.height = offH;
      const bctx = blended.getContext('2d');
      if (!bctx) {
        // 아트보드 클리핑 해제
        if (maskArtboard) {
          ctx.restore();
        }
        renderedMaskGroups.add(obj.id);
        continue;
      }
      
      // 백드롭 (배경 + 이전 레이어, 마스크 영역)
      if (bd) {
        bctx.drawImage(backdrop, 0, 0);
      }
      
      // 부모 비주얼 (마스크 객체 본체, 투명도는 마지막에 전체 적용)
      if (pctx) {
        bctx.drawImage(parentBase, 0, 0);
      }
      
      // 각 클리핑된 객체를 통합 블렌드 캔버스에 합성
      for (const clippedObj of clippedObjects) {
        const cObjX = Math.round(clippedObj.x);
        const cObjY = Math.round(clippedObj.y);
        const cObjW = Math.round(clippedObj.width);
        const cObjH = Math.round(clippedObj.height);
        if (!intersectsRect(cObjX, cObjY, cObjW, cObjH, viewLeft, viewTop, viewRight, viewBottom)) continue;
        
        // 자식 클리핑 레이어 회전 정보 확인
        const clippedRot = clippedObj.rotation || 0;
        const clippedHasRot = Math.abs(clippedRot) > 1e-6;
        
        // 자식 레이어 생성 (클리핑된 객체 렌더링)
        const childLayer = document.createElement('canvas');
        childLayer.width = offW;
        childLayer.height = offH;
        const clc = childLayer.getContext('2d');
        if (!clc) continue;
        
        clc.save();
        clc.translate(-left, -top);
        
        // 클리핑된 객체의 투명도 (블렌드 캔버스에서 사용)
        const childAlpha = Math.max(0, Math.min(1, (clippedObj.opacity ?? 100) / 100));
        
        // 클리핑된 객체 렌더링 (아웃글로우 포함, 투명도는 블렌드 캔버스에서 처리)
        if (clippedObj.outGlowEnabled) {
          renderOutGlow(clc, clippedObj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting);
        }
        
        // 클리핑된 객체 본체 렌더링 (오프스크린 캔버스 사용, 투명도는 블렌드 캔버스에서 처리)
        // 회전된 객체의 실제 경계 계산 (회전 변환 고려) - renderSingleObject와 동일한 방식
        let childOffW = Math.ceil(clippedObj.width);
        let childOffH = Math.ceil(clippedObj.height);
        if (clippedHasRot) {
          // 회전된 사각형의 4개 모서리를 회전 변환하여 실제 경계 계산
          const w = clippedObj.width;
          const h = clippedObj.height;
          const cos = Math.cos(clippedRot);
          const sin = Math.sin(clippedRot);
          
          // 중심 기준 모서리 좌표 (회전 전)
          const corners = [
            { x: -w / 2, y: -h / 2 },
            { x: w / 2, y: -h / 2 },
            { x: w / 2, y: h / 2 },
            { x: -w / 2, y: h / 2 }
          ];
          
          // 회전 변환 적용
          let minX = Infinity, maxX = -Infinity;
          let minY = Infinity, maxY = -Infinity;
          for (const corner of corners) {
            const rx = corner.x * cos - corner.y * sin;
            const ry = corner.x * sin + corner.y * cos;
            minX = Math.min(minX, rx);
            maxX = Math.max(maxX, rx);
            minY = Math.min(minY, ry);
            maxY = Math.max(maxY, ry);
          }
          
          childOffW = Math.ceil(maxX - minX);
          childOffH = Math.ceil(maxY - minY);
        }
        
        // 테두리 여유분 계산 (renderSingleObject와 동일한 방식)
        const childBorderWidth = clippedObj.borderWidth || 0;
        const childBorderPosition = clippedObj.borderPosition || 'center';
        let childExtra = 0;
        if (childBorderWidth > 0) {
          if (childBorderPosition === 'outside') {
            childExtra = Math.ceil(childBorderWidth * Math.sqrt(2));
          } else if (childBorderPosition === 'center') {
            childExtra = Math.ceil((childBorderWidth / 2) * Math.sqrt(2));
          } else {
            childExtra = 1;
          }
        }
        childExtra = Math.max(1, childExtra + 2);
        childOffW = Math.max(1, childOffW + childExtra * 2);
        childOffH = Math.max(1, childOffH + childExtra * 2);
        
        const childOff = document.createElement('canvas');
        childOff.width = childOffW;
        childOff.height = childOffH;
        
        const childOffCx = cObjX + cObjW / 2;
        const childOffCy = cObjY + cObjH / 2;
        const childOffLeft = Math.round(childOffCx - childOffW / 2);
        const childOffTop = Math.round(childOffCy - childOffH / 2);
        
        const childOffCtx = childOff.getContext('2d');
        if (childOffCtx) {
          childOffCtx.save();
          childOffCtx.translate(-childOffLeft, -childOffTop);
          renderObjectBody(childOffCtx, clippedObj, scale, isInteracting, interactingObjectIds);
          if (clippedObj.borderWidth && clippedObj.borderWidth > 0) {
            renderObjectBorder(childOffCtx, clippedObj);
          }
          childOffCtx.restore();
          
          clc.drawImage(childOff, childOffLeft, childOffTop);
        }
        
        clc.restore();
        
        // 마스크 영역으로 제한
        if (ma) {
          clc.save();
          clc.globalCompositeOperation = 'destination-in';
          clc.drawImage(maskAlphaCanvas, 0, 0);
          clc.globalCompositeOperation = 'source-over';
          clc.restore();
        }
        
        // 자식 레이어를 통합 블렌드 캔버스에 합성 (블렌드 모드 + 투명도 적용)
        const childBlendOp = getBlendModeOperation(clippedObj.blendMode);
        if (childBlendOp) {
          bctx.globalCompositeOperation = childBlendOp;
        }
        bctx.save();
        bctx.globalAlpha = childAlpha;
        bctx.drawImage(childLayer, 0, 0);
        bctx.restore();
        bctx.globalAlpha = 1;
        bctx.globalCompositeOperation = 'source-over';
      }
      
      // 마스크 객체 테두리를 통합 블렌드 캔버스에 포함
      if (obj.borderWidth && obj.borderWidth > 0) {
        const borderOff = document.createElement('canvas');
        borderOff.width = offW;
        borderOff.height = offH;
        const borderCtx = borderOff.getContext('2d');
        if (borderCtx) {
          borderCtx.save();
          borderCtx.translate(-left, -top);
          if (hasRot) {
            borderCtx.translate(cx, cy);
            borderCtx.rotate(rot);
            borderCtx.translate(-cx, -cy);
          }
          renderMaskObjectBorder(borderCtx, obj);
          borderCtx.restore();
          bctx.drawImage(borderOff, 0, 0);
        }
      }
      
      // 통합 블렌드 캔버스를 메인 캔버스에 합성 (마스크 객체의 블렌드 모드와 투명도 한 번만 적용)
      ctx.save();
      const maskBlendOp = getBlendModeOperation(obj.blendMode);
      if (maskBlendOp) {
        ctx.globalCompositeOperation = maskBlendOp;
      }
      if (maskAlpha !== 1) ctx.globalAlpha *= maskAlpha;
      ctx.drawImage(blended, left, top);
      ctx.restore();
      
      // 아트보드 클리핑 해제
      if (maskArtboard) {
        ctx.restore();
      }
      
      // 렌더링 완료 표시
      renderedMaskGroups.add(obj.id);
    } else {
      // 일반 객체 렌더링 (아트보드 클리핑 적용)
      if (obj.artboardId && artboardsMap) {
        const artboard = artboardsMap.get(obj.artboardId);
        if (artboard) {
          ctx.save();
          ctx.beginPath();
          ctx.rect(artboard.x, artboard.y, artboard.width, artboard.height);
          ctx.clip();
          renderSingleObject(ctx, obj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting, interactingObjectIds);
          ctx.restore();
        } else {
          renderSingleObject(ctx, obj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting, interactingObjectIds);
        }
      } else {
        renderSingleObject(ctx, obj, viewLeft, viewTop, viewRight, viewBottom, scale, isInteracting, interactingObjectIds);
      }
    }
  }
}

export function renderTempRect(
  ctx: CanvasRenderingContext2D,
  state: CanvasState,
  temp: { x: number; y: number; width: number; height: number } | null
) {
  if (!temp) return;
  const { viewLeft, viewTop, viewRight, viewBottom } = getViewportWorld(state, ctx.canvas as HTMLCanvasElement);
  // 정수 스냅
  const tx = Math.round(temp.x);
  const ty = Math.round(temp.y);
  const tw = Math.round(temp.width);
  const th = Math.round(temp.height);
  if (!intersectsRect(tx, ty, tw, th, viewLeft, viewTop, viewRight, viewBottom)) return;
  ctx.save();
  const color = ((temp as any).backgroundColor as string) || '#20c997';
  // 반투명 채우기
  ctx.globalAlpha = 1;
  ctx.fillStyle = color;
  if ((temp as any).type === 'circle') {
    const cx = Math.round(tx + tw / 2);
    const cy = Math.round(ty + th / 2);
    const rx = Math.max(0, Math.round(tw / 2));
    const ry = Math.max(0, Math.round(th / 2));
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.fillRect(tx, ty, tw, th);
  }

  // 점선 테두리(객체 색상)
  ctx.globalAlpha = 1;
  ctx.setLineDash([6, 6]);
  ctx.strokeStyle = '#00a0e9';
  ctx.lineWidth = 1;
  if ((temp as any).type === 'circle') {
    const cx = Math.round(tx + tw / 2);
    const cy = Math.round(ty + th / 2);
    const rx = Math.max(0, Math.round(tw / 2));
    const ry = Math.max(0, Math.round(th / 2));
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else {
    ctx.strokeRect(tx, ty, tw, th);
  }
  ctx.restore();
}

/**
 * 모든 렌더링 캐시 초기화
 * 템플릿 불러오기 시 호출하여 이전 캐시를 정리
 */
export function clearAllCaches(): void {
  outGlowCache.clear();
  blurCache.clear();
  imageDownscaleCache.clear();
}


